import { ReactNode, useEffect } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '@/pages/home/components/Navbar';
import Footer from '@/pages/home/components/Footer';

interface ArticleTemplateProps {
  title: string;
  subtitle?: string;
  description: string;
  canonicalUrl: string;
  breadcrumbLabel: string;
  imageSrc: string;
  imageAlt: string;
  publishedDate?: string;
  readTime?: string;
  children: ReactNode;
}

export default function ArticleTemplate({
  title,
  subtitle,
  description,
  canonicalUrl,
  breadcrumbLabel,
  imageSrc,
  imageAlt,
  publishedDate = 'Updated: August 2026',
  readTime = '10 min read',
  children,
}: ArticleTemplateProps) {
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' });
  }, []);

  return (
    <div className="min-h-screen bg-background-50">
      <Navbar />

      <main className="pt-20 pb-16">
        <article className="max-w-4xl mx-auto px-4 md:px-6">
          {/* Breadcrumb */}
          <nav className="mb-8 text-sm" aria-label="Breadcrumb">
            <ol className="flex items-center gap-2 text-foreground-500">
              <li><Link to="/" className="hover:text-primary-500 transition-colors duration-200">Home</Link></li>
              <li><span className="text-foreground-300">/</span></li>
              <li><span className="text-foreground-700">{breadcrumbLabel}</span></li>
            </ol>
          </nav>

          {/* Article Header */}
          <header className="mb-10">
            <h1 className="text-3xl md:text-4xl lg:text-5xl font-heading font-semibold text-foreground-900 mb-4 leading-tight">
              {title}
            </h1>
            {subtitle && (
              <p className="text-base md:text-lg text-foreground-600 mb-4 leading-relaxed">
                {subtitle}
              </p>
            )}
            <div className="flex flex-wrap items-center gap-4 text-sm text-foreground-500">
              <span className="flex items-center gap-1">
                <span className="w-4 h-4 flex items-center justify-center"><i className="ri-calendar-line"></i></span>
                {publishedDate}
              </span>
              <span className="flex items-center gap-1">
                <span className="w-4 h-4 flex items-center justify-center"><i className="ri-time-line"></i></span>
                {readTime}
              </span>
            </div>
          </header>

          {/* Featured Image */}
          <div className="mb-10 rounded-xl overflow-hidden">
            <img
              src={imageSrc}
              alt={imageAlt}
              className="w-full h-auto object-cover"
            />
          </div>

          {/* Article Content */}
          <div className="max-w-none">
            {children}
          </div>

          {/* Bottom CTA */}
          <section className="mt-12 bg-background-100 rounded-xl p-8 text-center border border-background-200/60">
            <h2 className="text-xl font-heading font-semibold text-foreground-900 mb-3">
              Ready to Start Shopping Smarter?
            </h2>
            <p className="text-foreground-600 text-sm mb-6 max-w-md mx-auto">
              Explore the Sugargoo Spreadsheet and discover thousands of organized product finds across all categories.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
              <Link
                to="/"
                className="px-6 py-3 bg-primary-500 hover:bg-primary-600 text-white font-semibold rounded-lg text-sm transition-all duration-200 whitespace-nowrap cursor-pointer"
              >
                Back to Homepage
              </Link>
              <a
                href="https://kaspreadsheet.com"
                target="_blank"
                rel="noopener nofollow"
                className="px-6 py-3 bg-accent-500 hover:bg-accent-600 text-white font-semibold rounded-lg text-sm transition-all duration-200 whitespace-nowrap cursor-pointer"
              >
                Visit Sugargoo Spreadsheet
              </a>
            </div>
          </section>
        </article>
      </main>

      <Footer />
    </div>
  );
}